import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, LogOut, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import {
  StepResponsavel, StepConjuge, StepFamilia, StepEndereco, StepEleitoral,
  StepSocial, StepPNE, StepIDOSO, StepRenda,
} from "./inscricao/Steps";

const STEP_COMPONENTS = {
  "Responsável": (p) => <StepResponsavel {...p} />,
  "Cônjuge": (p) => <StepConjuge {...p} isCasado={p.form.estado_civil === "Casado" || p.form.estado_civil === "União Estável"} />,
  "Família": (p) => <StepFamilia {...p} />,
  "Endereço": (p) => <StepEndereco {...p} onCepBlur={() => {}} />,
  "Eleitoral": (p) => <StepEleitoral {...p} />,
  "Social": (p) => <StepSocial {...p} />,
  "PNE": (p) => <StepPNE {...p} />,
  "Idoso": (p) => <StepIDOSO {...p} />,
  "Renda": (p) => <StepRenda {...p} rendaTotal={Number(p.form.renda_responsavel||0)+Number(p.form.renda_conjuge||0)+Number(p.form.outras_rendas||0)} />,
};

export default function CitizenUpdatePage() {
  const nav = useNavigate();
  const location = useLocation();
  // SEC: token e dados vêm EXCLUSIVAMENTE do navigation state (memória do
  // react-router). Nunca persistimos em sessionStorage/localStorage. Isso
  // garante que recarregar a página ou abrir diretamente a URL exige
  // reautenticação, reduzindo a janela de exposição em caso de XSS.
  const [token] = useState(() => location.state?.token || null);
  const [session, setSession] = useState(() => location.state?.session || null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token || !session) { nav("/consultar", { replace: true }); return; }
    setForm(session.inscricao || {});
    // Limpa o state da URL para evitar que token fique no histórico do browser
    nav(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permitidas = session?.sections_permitidas || [];
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const salvar = async () => {
    if (!token) { nav("/consultar", { replace: true }); return; }
    setSaving(true);
    try {
      const r = await api.put("/inscricoes/protocolo/self-update", { token, ...form });
      toast.success(`Atualizado! Campos: ${r.data.atualizado.length}`);
      // Após salvar, token foi consumido (JTI tracked no backend). Zera estado.
      setSession(null);
      nav("/consultar", { replace: true });
    } catch (e) {
      logger.error("Falha ao salvar atualização", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const sair = () => {
    setSession(null);
    nav("/consultar", { replace: true });
  };

  if (!session) return <div className="p-8 text-center">Carregando...</div>;

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="citizen-update-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black text-[#083CA6]">Atualização Cadastral</h1>
            <p className="text-sm text-slate-600">Protocolo <span className="font-mono">{session.protocolo}</span></p>
          </div>
          <Button variant="outline" onClick={sair}><LogOut className="w-4 h-4 mr-2" />Sair</Button>
        </div>

        {permitidas.length === 0 ? (
          <Alert className="border-yellow-500 bg-yellow-50">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>Nenhuma seção foi liberada pelo administrador para autoatualização no momento.</AlertDescription>
          </Alert>
        ) : (
          <>
            <Alert className="mb-6 border-[#083CA6]/30 bg-[#083CA6]/5">
              <AlertDescription className="text-sm">
                Você pode editar as seguintes seções: <strong>{permitidas.join(", ")}</strong>. Demais campos estão bloqueados por configuração da Prefeitura.
              </AlertDescription>
            </Alert>

            <div className="space-y-6">
              {permitidas.map((sec) => {
                const R = STEP_COMPONENTS[sec];
                if (!R) return null;
                return (
                  <Card key={sec} data-testid={`upd-sec-${sec}`}>
                    <CardHeader>
                      <CardTitle className="text-lg text-[#083CA6]">{sec}</CardTitle>
                    </CardHeader>
                    <CardContent>{R({ form, update })}</CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={salvar} disabled={saving} className="bg-[#00883A] hover:bg-[#007030]" data-testid="btn-salvar-atualizacao">
                <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alterações"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
